import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Loader2, Search, Receipt, AlertCircle, CheckCircle2, XCircle,
  RotateCcw, ShieldAlert, RefreshCw, Info, MailCheck, KeyRound,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";
import {
  getAllBkashPayments,
  getRefundEligibility,
  requestRefundCode,
  refundBkashPayment,
  getPaymentsAwaitingAccess,
  reconcilePaymentAccess,
  type AdminPaymentRecord,
  type BkashPaymentStatus,
} from "@/api/lms/bkash";

const STATUS_STYLES: Record<BkashPaymentStatus, string> = {
  COMPLETED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  REFUNDED: "bg-violet-100 text-violet-700 border-violet-200",
  INITIATED: "bg-amber-100 text-amber-700 border-amber-200",
  FAILED: "bg-rose-100 text-rose-700 border-rose-200",
  CANCELLED: "bg-stone-100 text-stone-600 border-stone-200",
};

const STATUS_FILTERS: Array<BkashPaymentStatus | "ALL"> = [
  "ALL", "COMPLETED", "REFUNDED", "FAILED", "CANCELLED", "INITIATED",
];

/** One signal row in the eligibility panel. */
function Signal({
  label, value, verdict, hint,
}: {
  label: string;
  value: string;
  verdict: "pass" | "fail" | "unknown";
  hint?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border/40 last:border-0">
      <div className="min-w-0">
        <div className="text-xs font-medium">{label}</div>
        {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-xs font-mono">{value}</span>
        {verdict === "pass" && <CheckCircle2 className="size-3.5 text-emerald-600" />}
        {verdict === "fail" && <XCircle className="size-3.5 text-rose-600" />}
        {verdict === "unknown" && <Info className="size-3.5 text-muted-foreground/60" />}
      </div>
    </div>
  );
}

export default function AdminPaymentsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<BkashPaymentStatus | "ALL">("ALL");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [refundReason, setRefundReason] = useState("");

  // Two-step confirmation dialog state.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);
  const [confirmationCode, setConfirmationCode] = useState("");

  const paymentsQuery = useQuery({
    queryKey: ["adminPayments"],
    queryFn: () => getAllBkashPayments(1, 200),
  });

  const awaitingQuery = useQuery({
    queryKey: ["paymentsAwaitingAccess"],
    queryFn: getPaymentsAwaitingAccess,
  });

  // Eligibility is fetched only for the payment the admin opened.
  const eligibilityQuery = useQuery({
    queryKey: ["refundEligibility", selectedOrderId],
    queryFn: () => getRefundEligibility(selectedOrderId as string),
    enabled: !!selectedOrderId,
  });

  // Step 1 — email a one-time code to the acting admin, then open the dialog.
  const requestCodeMutation = useMutation({
    mutationFn: () => requestRefundCode(selectedOrderId as string),
    onSuccess: (res) => {
      setCodeSentTo(res.sentTo);
      setConfirmationCode("");
      setConfirmOpen(true);
      toast.success(`Confirmation code sent to ${res.sentTo}`);
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not send confirmation code"),
  });

  // Step 2 — verify the code and process the refund.
  const refundMutation = useMutation({
    mutationFn: () =>
      refundBkashPayment(
        selectedOrderId as string,
        refundReason.trim(),
        confirmationCode.trim(),
      ),
    onSuccess: (res) => {
      toast.success(
        `Refund processed. ${res.accessRevoked ? "Access revoked." : "No active access to revoke."}` +
          (res.couponsReleased ? ` ${res.couponsReleased} coupon use released.` : ""),
      );
      setConfirmOpen(false);
      setSelectedOrderId(null);
      setRefundReason("");
      setConfirmationCode("");
      setCodeSentTo(null);
      queryClient.invalidateQueries({ queryKey: ["adminPayments"] });
    },
    // A wrong/expired code or bKash's own statusMessage surfaces here. The
    // dialog stays open so the admin can retype the code.
    onError: (e: any) => toast.error(e?.message ?? "Refund failed"),
  });

  const reconcileMutation = useMutation({
    mutationFn: (orderId?: string) => reconcilePaymentAccess(orderId),
    onSuccess: (res) => {
      toast.success(`Reconciled ${res.checked} order(s): ${res.granted} granted, ${res.stillFailing} still failing.`);
      queryClient.invalidateQueries({ queryKey: ["paymentsAwaitingAccess"] });
      queryClient.invalidateQueries({ queryKey: ["adminPayments"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Reconcile failed"),
  });

  if (!user || user.role !== "admin") return null;

  const payments = paymentsQuery.data?.items ?? [];
  const awaiting = awaitingQuery.data ?? [];

  const filtered = payments.filter((p: AdminPaymentRecord) => {
    if (statusFilter !== "ALL" && p.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      p.orderId?.toLowerCase().includes(q) ||
      p.trxId?.toLowerCase().includes(q) ||
      String(p.userId).includes(q) ||
      p.productId?.toLowerCase().includes(q)
    );
  });

  const selected = payments.find((p) => p.orderId === selectedOrderId) ?? null;
  const el = eligibilityQuery.data;
  const s = el?.signals;

  return (
    <DashboardLayout>
      <div className="dash-page">
        <div className="dash-page-header mb-6">
          <div className="min-w-0">
            <h1 className="dash-page-title flex items-center gap-2 truncate">
              <Receipt className="text-primary size-6 shrink-0" />
              Payments & Refunds
            </h1>
            <p className="dash-page-subtitle truncate">
              Review bKash payments and process refunds per the Refund Policy
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-xl shrink-0"
            disabled={paymentsQuery.isFetching}
            onClick={() => queryClient.invalidateQueries({ queryKey: ["adminPayments"] })}
          >
            <RefreshCw className={cn("size-3.5 mr-1", paymentsQuery.isFetching && "animate-spin")} />
            Refresh
          </Button>
        </div>

        {/* Paid but not delivered — needs attention first */}
        {awaiting.length > 0 && (
          <KCard className="p-5 space-y-3 border-amber-300 bg-amber-50/40">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Label className="text-xs font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1.5">
                <ShieldAlert className="size-4" />
                Paid but access not delivered ({awaiting.length})
              </Label>
              <Button
                size="sm"
                className="h-7 text-xs rounded-xl"
                disabled={reconcileMutation.isPending}
                onClick={() => reconcileMutation.mutate(undefined)}
              >
                {reconcileMutation.isPending
                  ? <Loader2 className="animate-spin size-3.5 mr-1" />
                  : <RotateCcw className="size-3.5 mr-1" />}
                Retry all
              </Button>
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {awaiting.map((a) => (
                <div key={a.orderId} className="flex items-center justify-between gap-3 text-xs bg-background/60 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-mono truncate">{a.orderId}</div>
                    <div className="text-[10px] text-rose-600 truncate">{a.accessError ?? "Unknown error"}</div>
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    className="h-6 text-[10px] rounded-lg shrink-0"
                    onClick={() => reconcileMutation.mutate(a.orderId)}
                  >
                    Retry
                  </Button>
                </div>
              ))}
            </div>
          </KCard>
        )}

        {/* Payment list */}
        <KCard className="p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Payments ({filtered.length})
            </Label>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex gap-1">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f}
                    onClick={() => setStatusFilter(f)}
                    className={cn(
                      "text-[10px] px-2 py-1 rounded-lg border font-semibold transition-colors",
                      statusFilter === f
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:bg-muted",
                    )}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="relative w-52">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                <Input
                  placeholder="Order ID, trxID, user…"
                  className="pl-8 h-7 text-xs rounded-xl"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {paymentsQuery.isPending ? (
            <div className="dash-empty-state flex flex-row items-center justify-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="animate-spin size-4" /> Loading payments…
            </div>
          ) : filtered.length === 0 ? (
            <div className="dash-empty-state">
              <Receipt className="size-10 mx-auto mb-4 opacity-30" />
              <p className="text-sm">No payments match this filter</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {filtered.map((p: AdminPaymentRecord) => (
                <div
                  key={p.orderId}
                  className={cn(
                    "flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border transition-colors",
                    selectedOrderId === p.orderId
                      ? "border-primary bg-primary/5"
                      : "border-border/60 bg-muted/20 hover:bg-muted/40",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={cn(
                        "text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border",
                        STATUS_STYLES[p.status],
                      )}>
                        {p.status}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {p.productType === "COURSE" ? "Course" : "Mock Test"}
                      </span>
                      {p.couponCode && (
                        <span className="text-[9px] bg-muted px-1.5 py-0.5 rounded font-mono">
                          {p.couponCode}
                        </span>
                      )}
                      {!p.accessGranted && p.status === "COMPLETED" && (
                        <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">
                          NO ACCESS
                        </span>
                      )}
                    </div>
                    <div className="text-xs font-mono truncate mt-1">{p.orderId}</div>
                    <div className="text-[10px] text-muted-foreground flex flex-wrap gap-2">
                      <span>User #{p.userId}</span>
                      <span>·</span>
                      <span className="font-mono">trx: {p.trxId ?? "—"}</span>
                      <span>·</span>
                      <span>{p.paidAt ? new Date(p.paidAt).toLocaleDateString() : "unpaid"}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className="text-sm font-bold font-mono">
                        {p.currency} {p.amount}
                      </div>
                      {p.status === "REFUNDED" && p.refundedAt && (
                        <div className="text-[9px] text-violet-600">
                          refunded {new Date(p.refundedAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={selectedOrderId === p.orderId ? "default" : "outline"}
                      className="h-7 text-[10px] rounded-lg"
                      disabled={p.status !== "COMPLETED"}
                      onClick={() => {
                        setSelectedOrderId(selectedOrderId === p.orderId ? null : p.orderId);
                        setRefundReason("");
                      }}
                    >
                      {p.status === "REFUNDED" ? "Refunded" : "Review refund"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </KCard>

        {/* Refund decision panel */}
        {selectedOrderId && selected && (
          <KCard className="p-5 space-y-4 border-primary/40">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Refund review — <span className="font-mono normal-case">{selected.orderId}</span>
              </Label>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedOrderId(null)}>
                Close
              </Button>
            </div>

            {eligibilityQuery.isPending ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
                <Loader2 className="animate-spin size-4" /> Loading eligibility signals…
              </div>
            ) : !el || !s ? (
              <p className="text-sm text-rose-600">Could not load eligibility data.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Signals */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                      Policy signals
                    </div>
                    <Signal
                      label="Within 7-day window"
                      hint={`${s.daysSincePurchase ?? "?"} day(s) since purchase`}
                      value={s.withinSevenDayWindow === null ? "unknown" : s.withinSevenDayWindow ? "Yes" : "No"}
                      verdict={s.withinSevenDayWindow === null ? "unknown" : s.withinSevenDayWindow ? "pass" : "fail"}
                    />
                    {el.productType === "COURSE" ? (
                      <Signal
                        label="Under 20% content viewed"
                        hint={`Progress: ${s.courseProgressPercent ?? 0}%`}
                        value={s.underTwentyPercentViewed === null ? "unknown" : s.underTwentyPercentViewed ? "Yes" : "No"}
                        verdict={s.underTwentyPercentViewed === null ? "unknown" : s.underTwentyPercentViewed ? "pass" : "fail"}
                      />
                    ) : (
                      <Signal
                        label="Mock test unattempted"
                        hint={`Attempts used: ${s.mockAttemptsUsed ?? 0} / ${s.mockPurchasedAttempts ?? 0}`}
                        value={s.mockUnattempted === null ? "unknown" : s.mockUnattempted ? "Yes" : "No"}
                        verdict={s.mockUnattempted === null ? "unknown" : s.mockUnattempted ? "pass" : "fail"}
                      />
                    )}
                    <Signal
                      label="Duplicate payment"
                      hint="Auto full-refund case if yes"
                      value={s.isDuplicatePayment ? "Yes" : "No"}
                      verdict={s.isDuplicatePayment ? "pass" : "unknown"}
                    />
                    <Signal
                      label="Access was delivered"
                      hint="Not delivered = auto full-refund case"
                      value={el.accessDelivered ? "Yes" : "No"}
                      verdict={el.accessDelivered ? "unknown" : "pass"}
                    />
                    <Signal label="Certificate issued" value="not tracked" verdict="unknown" hint="Verify manually" />
                    <Signal label="Resources downloaded" value="not tracked" verdict="unknown" hint="Verify manually" />
                    <Signal label="Live class attended" value="not tracked" verdict="unknown" hint="Verify manually" />
                  </div>

                  {/* Payment facts */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                      Payment
                    </div>
                    <Signal label="Amount charged" value={`${selected.currency} ${el.amountPaid}`} verdict="unknown" />
                    {el.originalAmount && el.originalAmount !== el.amountPaid && (
                      <Signal label="List price" value={`${selected.currency} ${el.originalAmount}`} verdict="unknown" />
                    )}
                    {el.couponCode && (
                      <Signal
                        label="Coupon used"
                        value={el.couponCode}
                        verdict="unknown"
                        hint="Its usage will be released on refund"
                      />
                    )}
                    <Signal label="bKash trxID" value={el.trxId ?? "—"} verdict="unknown" />
                    <Signal label="Student" value={`#${el.userId}`} verdict="unknown" />
                    <Signal
                      label="Paid at"
                      value={el.paidAt ? new Date(el.paidAt).toLocaleString() : "—"}
                      verdict="unknown"
                    />

                    <div className="mt-3 text-[10px] text-muted-foreground flex gap-1.5 items-start bg-muted/40 rounded-lg p-2">
                      <Info className="size-3 shrink-0 mt-0.5" />
                      <span>{el.note}</span>
                    </div>
                  </div>
                </div>

                {/* Action */}
                <div className="border-t border-border pt-4 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">
                      Refund reason <span className="text-muted-foreground font-normal">(from the student's email — stored in the audit record)</span>
                    </Label>
                    <Input
                      placeholder="e.g. Duplicate payment — student charged twice on 21 Jul"
                      className="h-9 text-sm rounded-xl"
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                    />
                  </div>

                  <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                    <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
                    <span>
                      This returns <strong>{selected.currency} {el.amountPaid}</strong> to the student's bKash
                      account and immediately revokes their access to this{" "}
                      {el.productType === "COURSE" ? "course" : "mock test"}. This cannot be undone.
                    </span>
                  </div>

                  <Button
                    className="rounded-xl font-bold text-xs h-9"
                    variant="destructive"
                    disabled={
                      !el.refundable ||
                      refundReason.trim().length < 3 ||
                      requestCodeMutation.isPending
                    }
                    onClick={() => requestCodeMutation.mutate()}
                  >
                    {requestCodeMutation.isPending
                      ? <Loader2 className="animate-spin size-3.5 mr-1" />
                      : <RotateCcw className="size-3.5 mr-1" />}
                    Process refund
                  </Button>
                  {refundReason.trim().length < 3 && (
                    <p className="text-[10px] text-muted-foreground">A reason is required before refunding.</p>
                  )}
                </div>
              </>
            )}
          </KCard>
        )}

        {/* Email-code confirmation dialog for the refund */}
        <Dialog
          open={confirmOpen}
          onOpenChange={(open) => {
            // Never close mid-request — the refund is in flight.
            if (refundMutation.isPending) return;
            setConfirmOpen(open);
            if (!open) setConfirmationCode("");
          }}
        >
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <ShieldAlert className="size-5 text-destructive" />
                Confirm refund
              </DialogTitle>
              <DialogDescription className="text-xs">
                This action returns money and revokes access. It cannot be undone.
              </DialogDescription>
            </DialogHeader>

            {selected && el && (
              <div className="space-y-4">
                {/* Restate exactly what is about to happen */}
                <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-mono font-bold">
                      {selected.currency} {el.amountPaid}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Student</span>
                    <span className="font-mono">#{el.userId}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Product</span>
                    <span>{el.productType === "COURSE" ? "Course" : "Mock Test"}</span>
                  </div>
                  <div className="flex justify-between text-xs gap-3">
                    <span className="text-muted-foreground shrink-0">Order</span>
                    <span className="font-mono truncate">{el.orderId}</span>
                  </div>
                  {el.couponCode && (
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Coupon released</span>
                      <span className="font-mono">{el.couponCode}</span>
                    </div>
                  )}
                </div>

                {/* Code entry */}
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-2">
                    <MailCheck className="size-3.5 shrink-0" />
                    <span>
                      A 6-digit code was sent to <strong>{codeSentTo}</strong>. It expires in 10 minutes.
                    </span>
                  </div>

                  <Label className="text-xs font-semibold flex items-center gap-1.5">
                    <KeyRound className="size-3.5" />
                    Confirmation code
                  </Label>
                  <Input
                    autoFocus
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    className="h-11 text-center text-lg font-mono tracking-[0.4em] rounded-xl"
                    value={confirmationCode}
                    onChange={(e) =>
                      setConfirmationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        confirmationCode.length === 6 &&
                        !refundMutation.isPending
                      ) {
                        refundMutation.mutate();
                      }
                    }}
                  />

                  <button
                    type="button"
                    className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:opacity-50"
                    disabled={requestCodeMutation.isPending || refundMutation.isPending}
                    onClick={() => requestCodeMutation.mutate()}
                  >
                    {requestCodeMutation.isPending ? "Sending…" : "Resend code"}
                  </button>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                className="rounded-xl text-xs h-9"
                disabled={refundMutation.isPending}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="rounded-xl text-xs h-9 font-bold"
                disabled={confirmationCode.length !== 6 || refundMutation.isPending}
                onClick={() => refundMutation.mutate()}
              >
                {refundMutation.isPending
                  ? <Loader2 className="animate-spin size-3.5 mr-1" />
                  : <RotateCcw className="size-3.5 mr-1" />}
                {refundMutation.isPending ? "Processing…" : "Confirm refund"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
